// Механика обучающих туров, общая для консоли (apps/admin) и кабинета
// продавца (apps/seller-web): где нарисовать прожектор и куда поставить
// подсказку, чтобы она не вылезала за экран и не перекрывала подсвеченный
// элемент. Здесь только геометрия и слежение за DOM — вид подсказки каждое
// приложение рисует в своей палитре.
//
// Почему своё, а не «bottom, если снизу 180px»: прежняя эвристика не знала
// реальной высоты подсказки и не учитывала элементы выше экрана (таблица
// товаров, лента блоков) — подсказка уезжала на −600px или ниже экрана.

export type TourBox = { top: number; left: number; width: number; height: number }
export type TourSide = "top" | "bottom" | "left" | "right"
/** inside — элемент занимает весь экран, подсказка лежит поверх него. */
export type TourPlacement = TourSide | "center" | "inside"

export type TourLayoutOptions = {
  /** Отступ прожектора вокруг элемента. */
  padding: number
  /** Зазор между прожектором и подсказкой. */
  gap: number
  /** Минимальный зазор подсказки от краёв экрана. */
  margin: number
  /** Сколько сверху занято липкой шапкой — туда подсказку не ставим. */
  safeTop?: number
}

export type TourLayout = {
  placement: TourPlacement
  /** Прожектор в координатах экрана, обрезанный по видимой области. */
  spotlight: TourBox | null
  tip: { top: number; left: number }
  /** Стрелка: сторона подсказки и смещение центра стрелки вдоль неё. */
  arrow: { side: TourSide; offset: number } | null
}

const clamp = (value: number, min: number, max: number) =>
  max < min ? min : Math.min(Math.max(value, min), max)

/**
 * Раскладка шага тура. target — прямоугольник элемента (getBoundingClientRect),
 * tip — реальный размер подсказки, viewport — размер окна.
 */
export function computeTourLayout(
  target: TourBox | null,
  tip: { width: number; height: number },
  viewport: { width: number; height: number },
  options: TourLayoutOptions,
): TourLayout {
  const { padding, gap, margin } = options
  const safeTop = Math.max(0, options.safeTop ?? 0)
  const vw = viewport.width
  const vh = viewport.height
  const centered = (): TourLayout => ({
    placement: "center",
    spotlight: null,
    tip: {
      top: clamp((vh - tip.height) / 2, margin, vh - tip.height - margin),
      left: clamp((vw - tip.width) / 2, margin, vw - tip.width - margin),
    },
    arrow: null,
  })
  if (!target || target.width <= 0 || target.height <= 0) return centered()

  // Прожектор обрезаем по экрану: у таблицы выше экрана рамка иначе уходит
  // за край и подсветки не видно.
  const edge = 2
  const top = Math.max(target.top - padding, safeTop + edge)
  const left = Math.max(target.left - padding, edge)
  const bottom = Math.min(target.top + target.height + padding, vh - edge)
  const right = Math.min(target.left + target.width + padding, vw - edge)
  if (bottom - top < 4 || right - left < 4) return centered()
  const spotlight: TourBox = { top, left, width: right - left, height: bottom - top }

  const space: Record<TourSide, number> = {
    bottom: vh - bottom - gap - margin,
    top: top - safeTop - gap - margin,
    right: vw - right - gap - margin,
    left: left - gap - margin,
  }
  const fits: Record<TourSide, boolean> = {
    bottom: space.bottom >= tip.height,
    top: space.top >= tip.height,
    right: space.right >= tip.width && vh - safeTop - margin * 2 >= tip.height,
    left: space.left >= tip.width && vh - safeTop - margin * 2 >= tip.height,
  }
  // Снизу — привычнее всего; сверху — если снизу не лезет; сбоку — для
  // высоких узких элементов (колонка, сайдбар).
  const side = (["bottom", "top", "right", "left"] as const).find((s) => fits[s])

  // Стрелка смотрит в центр элемента, но не за пределы видимой рамки.
  const cx = clamp(target.left + target.width / 2, left, right)
  const cy = clamp(target.top + target.height / 2, top, bottom)
  const minLeft = margin
  const maxLeft = vw - tip.width - margin
  const minTop = safeTop + margin
  const maxTop = vh - tip.height - margin
  const ARROW_INSET = 18

  if (!side) {
    // Элемент почти во весь экран: кладём подсказку поверх него у нижнего
    // края видимой части, без стрелки — рамка и так показывает, о чём речь.
    return {
      placement: "inside",
      spotlight,
      tip: {
        top: clamp(bottom - tip.height - margin, minTop, maxTop),
        left: clamp(cx - tip.width / 2, minLeft, maxLeft),
      },
      arrow: null,
    }
  }

  if (side === "bottom" || side === "top") {
    const tipLeft = clamp(cx - tip.width / 2, minLeft, maxLeft)
    const tipTop = side === "bottom" ? bottom + gap : top - gap - tip.height
    return {
      placement: side,
      spotlight,
      tip: { top: clamp(tipTop, minTop, maxTop), left: tipLeft },
      arrow: {
        side: side === "bottom" ? "top" : "bottom",
        offset: clamp(cx - tipLeft, ARROW_INSET, tip.width - ARROW_INSET),
      },
    }
  }

  const tipTop = clamp(cy - tip.height / 2, minTop, maxTop)
  const tipLeft = side === "right" ? right + gap : left - gap - tip.width
  return {
    placement: side,
    spotlight,
    tip: { top: tipTop, left: clamp(tipLeft, minLeft, maxLeft) },
    arrow: {
      side: side === "right" ? "left" : "right",
      offset: clamp(cy - tipTop, ARROW_INSET, tip.height - ARROW_INSET),
    },
  }
}

/** Элемент есть на странице и виден (у скрытого через display:none прямоугольник нулевой). */
export function isTourTargetVisible(selector: string): boolean {
  if (typeof document === "undefined") return false
  const el = document.querySelector<HTMLElement>(selector)
  if (!el) return false
  const rect = el.getBoundingClientRect()
  return rect.width > 0 && rect.height > 0
}

/** Высота липкой шапки, помеченной data-tour-safe-top, — её подсказка не перекрывает. */
export function tourSafeTop(): number {
  if (typeof document === "undefined") return 0
  let safe = 0
  for (const el of Array.from(document.querySelectorAll<HTMLElement>("[data-tour-safe-top]"))) {
    const r = el.getBoundingClientRect()
    if (r.height > 0 && r.top <= 1) safe = Math.max(safe, r.bottom)
  }
  return safe
}

/** Элемент обрезан прокручиваемым предком (широкая таблица с горизонтальной прокруткой). */
function clippedByScroller(el: HTMLElement, r: DOMRect): boolean {
  for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
    const style = getComputedStyle(node)
    if (style.overflowX === "visible" && style.overflowY === "visible") continue
    const p = node.getBoundingClientRect()
    if (r.left < p.left - 1 || r.right > p.right + 1 || r.top < p.top - 1 || r.bottom > p.bottom + 1)
      return true
  }
  return false
}

/**
 * Следит за целью шага: докручивает к ней страницу, ждёт появления (ленивые
 * блоки дорисовываются после загрузки) и сообщает её прямоугольник на каждом
 * кадре прокрутки, на resize и при изменении размеров элемента. Возвращает
 * функцию отписки.
 */
export function trackTourTarget(
  selector: string,
  onRect: (rect: TourBox | null) => void,
  options: { tipHeight?: number; waitMs?: number } = {},
): () => void {
  let stopped = false
  let frame = 0
  let observer: ResizeObserver | null = null
  let observed: Element | null = null
  let last = ""

  const find = () => document.querySelector<HTMLElement>(selector)
  const emit = () => {
    if (stopped) return
    const el = find()
    if (el && el !== observed && typeof ResizeObserver !== "undefined") {
      observer?.disconnect()
      observer = new ResizeObserver(() => emit())
      observer.observe(el)
      observed = el
    }
    const r = el?.getBoundingClientRect()
    const rect = r && r.width > 0 && r.height > 0
      ? { top: r.top, left: r.left, width: r.width, height: r.height }
      : null
    const key = rect ? `${rect.top}|${rect.left}|${rect.width}|${rect.height}` : "none"
    if (key === last) return
    last = key
    onRect(rect)
  }

  const scrollTo = (el: HTMLElement) => {
    const r = el.getBoundingClientRect()
    const safe = tourSafeTop()
    const room = window.innerHeight - safe - (options.tipHeight ?? 200) - 48
    // Помещается вместе с подсказкой — ставим по центру; высокий элемент —
    // верхним краем под шапку, чтобы было видно начало.
    const fits = r.height <= room
    const inView =
      r.top >= safe + 8 &&
      r.bottom <= window.innerHeight - 8 &&
      r.left >= 0 &&
      r.right <= window.innerWidth &&
      !clippedByScroller(el, r)
    if (inView && fits) return
    const prev = el.style.scrollMarginTop
    el.style.scrollMarginTop = `${safe + 16}px`
    el.scrollIntoView({ behavior: "smooth", block: fits ? "center" : "start", inline: "nearest" })
    el.style.scrollMarginTop = prev
  }

  // Пока идёт плавная прокрутка, снимаем координаты каждый кадр — события
  // scroll приходят не на всех контейнерах.
  const pump = (until: number) => {
    emit()
    if (!stopped && performance.now() < until) frame = requestAnimationFrame(() => pump(until))
  }

  const started = performance.now()
  const waitFor = () => {
    if (stopped) return
    const el = find()
    if (el && el.getBoundingClientRect().width > 0) {
      scrollTo(el)
      pump(performance.now() + 900)
      return
    }
    if (performance.now() - started > (options.waitMs ?? 1200)) {
      emit()
      return
    }
    frame = requestAnimationFrame(waitFor)
  }
  waitFor()

  const onMove = () => emit()
  window.addEventListener("resize", onMove)
  window.addEventListener("scroll", onMove, true)
  return () => {
    stopped = true
    cancelAnimationFrame(frame)
    observer?.disconnect()
    window.removeEventListener("resize", onMove)
    window.removeEventListener("scroll", onMove, true)
  }
}

/**
 * Поповер «?» под кнопкой, а если снизу не помещается (кнопка у нижнего края,
 * как у заголовка профиля на телефоне) — над ней. По горизонтали — от левого
 * края кнопки с зажимом в экран.
 */
export function placeHelpPopover(
  button: TourBox,
  popover: { width: number; height: number },
  viewport: { width: number; height: number },
  margin = 12,
  gap = 8,
): { top: number; left: number } {
  const below = button.top + button.height + gap
  const fitsBelow = below + popover.height <= viewport.height - margin
  const above = button.top - gap - popover.height
  const top = fitsBelow || above < margin ? below : above
  return {
    top: clamp(top, margin, viewport.height - popover.height - margin),
    left: clamp(button.left, margin, viewport.width - popover.width - margin),
  }
}
